// HotTopics.jsx — the Brands → Hot Topics submenu page at
// /admin/brands-settings/hot-topics.
//
// Lists the recurring topics the worker's weekly Hot Topics crawl extracts
// from each brand's inbound channels (email/chat/social). Each row shows the
// topic, where it was mentioned (references with links/urls), how many times
// (mentions), and the brand it belongs to. The "Assign" button pins a topic to
// that brand's BrandSettings.hottestTopic (which the enricher then reads).

import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  listHotTopics,
  analyzeHotTopics,
  assignHotTopic,
  deleteHotTopic,
} from "../../api/hotTopics/hotTopics";

function fmtTime(t) {
  if (!t) return "—";
  try { return new Date(t).toLocaleString(); } catch { return ""; }
}

function References({ refs }) {
  const list = Array.isArray(refs) ? refs.slice(0, 6) : [];
  if (!list.length) return <span style={{ color: "#999" }}>—</span>;
  return (
    <ul className="ht-refs">
      {list.map((r, i) => (
        <li key={i}>
          <span className="ht-src">{r.source || "other"}</span>
          {r.url ? (
            <a href={r.url} target="_blank" rel="noopener noreferrer">
              {r.label || r.url}
            </a>
          ) : (
            <span>{r.label || ""}</span>
          )}
          {r.excerpt ? <span className="ht-excerpt"> — “{r.excerpt.slice(0, 120)}”</span> : null}
        </li>
      ))}
    </ul>
  );
}

export default function HotTopics() {
  const userInfoRedux = useSelector((s) => s.user.userInfo);
  const [rows, setRows] = useState([]);
  const [brandId, setBrandId] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [err, setErr] = useState(null);

  const refresh = useCallback(async () => {
    setBusy(true); setErr(null);
    try {
      const r = await listHotTopics(brandId ? { brand_id: brandId } : {});
      setRows((r && r.data) || []);
    } catch (e) { setErr((e && (e.error || e.message)) || "Failed to load"); }
    finally { setBusy(false); }
  }, [brandId]);

  useEffect(() => { refresh(); }, [refresh]);

  const flash = (m) => { setMsg(m); setTimeout(() => setMsg(null), 3500); };

  const onAssign = async (id) => {
    setErr(null);
    try { await assignHotTopic(id); flash("✓ assigned as the brand's hottest topic"); refresh(); }
    catch (e) { setErr((e && (e.error || e.message)) || "Assign failed"); }
  };
  const onDelete = async (id) => {
    setErr(null);
    try { await deleteHotTopic(id); refresh(); }
    catch (e) { setErr((e && (e.error || e.message)) || "Delete failed"); }
  };
  const onAnalyze = async () => {
    setErr(null);
    if (!brandId) { setErr("Enter a brand id to analyze, or wait for the weekly crawl."); return; }
    try { await analyzeHotTopics(brandId); flash("✓ analysis queued — refresh in a moment"); }
    catch (e) { setErr((e && (e.error || e.message)) || "Analyze failed"); }
  };

  return (
    <div className="container py-4">
      <style>{`
        .ht-card { background:#fff; border:1px solid #e5e5e5; border-radius:8px; padding:14px; margin-bottom:12px; }
        .ht-table { width:100%; font-size:13px; }
        .ht-table th { font-size:11px; color:#888; text-transform:uppercase; text-align:left; font-weight:500; border-bottom:1px solid #f1f1f1; padding:6px 8px; }
        .ht-table td { padding:8px 8px; border-bottom:1px solid #f4f4f4; vertical-align:top; }
        .ht-btn { font-size:12px; padding:4px 10px; border:1px solid #ddd; background:#fff; border-radius:4px; cursor:pointer; color:#525252; }
        .ht-btn:hover:not(:disabled){ background:#f7f9fc; border-color:#bbb; }
        .ht-btn-primary { color:#fff; background:#0F62FE; border-color:#0F62FE; }
        .ht-btn-primary:hover:not(:disabled){ background:#0846b3; }
        .ht-btn-danger { color:#c0392b; border-color:#f1c4c0; }
        .ht-pill { display:inline-block; font-size:11px; padding:1px 8px; border-radius:10px; background:#eef3ff; color:#0846b3; }
        .ht-assigned { background:#e8f7ee; color:#1e6b3e; }
        .ht-refs { list-style:none; padding:0; margin:0; }
        .ht-refs li { margin-bottom:3px; }
        .ht-src { display:inline-block; min-width:46px; font-size:10px; text-transform:uppercase; color:#888; margin-right:6px; }
        .ht-excerpt { color:#777; }
        .ht-input { font-size:13px; padding:5px 8px; border:1px solid #ddd; border-radius:4px; }
      `}</style>

      <div className="d-flex align-items-baseline justify-content-between mb-3">
        <h3 className="mb-0">Hot Topics</h3>
        <div style={{ fontSize: 12, color: "#888" }}>
          {userInfoRedux?.superAdmin ? "super admin · all brands" : "your brands"} · refreshed weekly
        </div>
      </div>

      <div className="ht-card d-flex align-items-center" style={{ gap: 8, flexWrap: "wrap" }}>
        <input
          className="ht-input"
          placeholder="Filter by brand id (optional)"
          value={brandId}
          onChange={(e) => setBrandId(e.target.value.trim())}
          style={{ minWidth: 260 }}
        />
        <button className="ht-btn" onClick={refresh} disabled={busy}>Refresh</button>
        <button className="ht-btn ht-btn-primary" onClick={onAnalyze} disabled={busy}>Analyze now</button>
      </div>

      {msg && <div className="alert alert-info" style={{ fontSize: 13 }}>{msg}</div>}
      {err && <div className="alert alert-warning" style={{ fontSize: 13 }}>{err}</div>}

      <div className="ht-card">
        <table className="ht-table">
          <thead>
            <tr>
              <th>Topic</th>
              <th>Where mentioned</th>
              <th>Mentions</th>
              <th>Brand</th>
              <th>Sentiment</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan="6" style={{ color: "#999", padding: 20, textAlign: "center" }}>
                {busy ? "Loading…" : "No hot topics yet. They appear after the weekly crawl (or click Analyze now for a brand)."}
              </td></tr>
            )}
            {rows.map((r) => (
              <tr key={r._id}>
                <td>
                  <strong>{r.topic || r.key}</strong>
                  {r.assigned ? <span className="ht-pill ht-assigned" style={{ marginLeft: 6 }}>assigned</span> : null}
                  {r.intent ? <div style={{ fontSize: 11, color: "#888" }}>{r.intent}</div> : null}
                  {r.description ? <div style={{ fontSize: 12, color: "#666" }}>{r.description}</div> : null}
                </td>
                <td><References refs={r.references} /></td>
                <td><span className="ht-pill">{r.mentions || 0}</span></td>
                <td>{r.brandName || r.brand_id || "—"}</td>
                <td style={{ whiteSpace: "nowrap" }}>{r.sentiment || "—"}</td>
                <td style={{ whiteSpace: "nowrap", textAlign: "right" }}>
                  <button className="ht-btn ht-btn-primary" onClick={() => onAssign(r._id)} disabled={busy || r.assigned}>
                    {r.assigned ? "Assigned" : "Assign"}
                  </button>
                  <button className="ht-btn ht-btn-danger" style={{ marginLeft: 6 }} onClick={() => onDelete(r._id)} disabled={busy}>
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ fontSize: 11, color: "#aaa", marginTop: 8 }}>
          Last updated {rows[0] ? fmtTime(rows[0].lastSeenAt || rows[0].updatedAt) : "—"}
        </div>
      </div>
    </div>
  );
}
